import type { DataExportRegistryItem } from "../types/dataExport";

/**
 * The registry API returns internal labels and category keys. These maps hold
 * the customer-facing wording for the Data Exports screen; anything missing
 * falls back to the registry values so new export types still appear.
 */
export const EXPORT_TYPE_LABELS: Record<string, string> = {
  all_exports_zip: "All Account Data (zip)",
  buylist_history: "Buylist History",
  buylist_history_cascade: "Buylist History",
  buylist_rules: "Buylist Rules",
  customers: "Customer Data",
  customers_cascade: "Customer Data",
  condition_variants: "Condition Variants",
  pricing_config: "Pricing Data",
  staff_users: "Staff Users",
  search_filters: "Search Filters",
  store_settings: "Store Settings",
  till_config: "Till Data",
  cash_drawers: "Cash Drawers",
  cash_drawers_cascade: "Cash Drawers",
  store_credit: "Store Credit History",
  store_credit_cascade: "Store Credit History",
  inventory: "Inventory Data",
  inventory_cascade: "Inventory Data",
  events: "Event Data",
  events_cascade: "Event Data",
};

export const EXPORT_CATEGORY_LABELS: Record<string, string> = {
  composite: "All Account Data",
  buylist: "Buylist",
  customer: "Customer Data",
  config: "Configuration",
  financial: "Financial",
  inventory: "Inventory",
  operations: "Operations",
};

/** Category display order; unlisted categories render afterwards. */
export const EXPORT_CATEGORY_ORDER = [
  "composite",
  "buylist",
  "customer",
  "config",
  "financial",
  "inventory",
  "operations",
];

/** Option order within a category; unlisted exports render afterwards. */
export const EXPORT_TYPE_ORDER = [
  "all_exports_zip",
  "buylist_history_cascade",
  "buylist_history",
  "buylist_rules",
  "customers",
  "customers_cascade",
  "condition_variants",
  "pricing_config",
  "staff_users",
  "search_filters",
  "store_settings",
  "till_config",
  "cash_drawers_cascade",
  "cash_drawers",
  "store_credit",
  "store_credit_cascade",
  "inventory_cascade",
  "inventory",
  "events",
  "events_cascade",
];

export const exportTypeLabel = (item: DataExportRegistryItem): string =>
  EXPORT_TYPE_LABELS[item.id] ?? item.label;

/** Fallbacks when the registry omits batch size bounds (matches API defaults). */
export const MIN_BATCH_SIZE = 500;
export const MAX_BATCH_SIZE = 5000;
export const DEFAULT_BATCH_SIZE = 1000;

export type BatchSizeLimits = {
  min: number;
  max: number;
  default: number;
  /** False when the selected exports' ranges do not overlap at all. */
  hasCommonRange: boolean;
  /** Exports the limits were derived from, for error messaging. */
  labels: string[];
};

/**
 * Resolves the batch size bounds for a set of selected exports.
 *
 * A single batch size is sent to every selected export, so the usable range is
 * the intersection of their individual ranges — not the range of whichever
 * export happens to come first.
 */
export const resolveBatchSizeLimits = (
  selected: DataExportRegistryItem[]
): BatchSizeLimits | null => {
  const batchDescriptors = selected.filter((item) => item.supportsBatchSize);
  if (!batchDescriptors.length) return null;

  const min = Math.max(
    ...batchDescriptors.map((item) => item.batchSizeMin ?? MIN_BATCH_SIZE)
  );
  const max = Math.min(
    ...batchDescriptors.map((item) => item.batchSizeMax ?? MAX_BATCH_SIZE)
  );
  const smallestDefault = Math.min(
    ...batchDescriptors.map(
      (item) => item.batchSizeDefault ?? DEFAULT_BATCH_SIZE
    )
  );

  return {
    min,
    max,
    default: Math.min(Math.max(smallestDefault, min), max),
    hasCommonRange: min <= max,
    labels: batchDescriptors.map(exportTypeLabel),
  };
};

/** Label for a history row, which only carries the export id. */
export const exportLabelForId = (id: string, fallback: string): string =>
  EXPORT_TYPE_LABELS[id] ?? fallback;

export const exportCategoryLabel = (category: string): string =>
  EXPORT_CATEGORY_LABELS[category] ??
  category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, " ");

const orderIndex = (order: string[], key: string) => {
  const index = order.indexOf(key);
  return index === -1 ? order.length : index;
};

export type ExportTypeGroup = {
  category: string;
  label: string;
  items: DataExportRegistryItem[];
};

/** Groups registry entries into the categories and order shown in the UI. */
export const buildExportTypeGroups = (
  registry: DataExportRegistryItem[]
): ExportTypeGroup[] => {
  const byCategory = new Map<string, DataExportRegistryItem[]>();
  for (const item of registry) {
    const items = byCategory.get(item.category);
    if (items) items.push(item);
    else byCategory.set(item.category, [item]);
  }

  return Array.from(byCategory.entries())
    .sort(([a], [b]) => {
      const diff =
        orderIndex(EXPORT_CATEGORY_ORDER, a) -
        orderIndex(EXPORT_CATEGORY_ORDER, b);
      return diff !== 0 ? diff : a.localeCompare(b);
    })
    .map(([category, items]) => ({
      category,
      label: exportCategoryLabel(category),
      items: [...items].sort((a, b) => {
        const diff =
          orderIndex(EXPORT_TYPE_ORDER, a.id) -
          orderIndex(EXPORT_TYPE_ORDER, b.id);
        return diff !== 0
          ? diff
          : exportTypeLabel(a).localeCompare(exportTypeLabel(b));
      }),
    }));
};

/** Copy for a multi-select submit where some exports started and others did not. */
export const summarizeBatchExportResult = (
  startedCount: number,
  totalCount: number,
  failedLabels: string[]
): string | null => {
  if (totalCount <= 1 || failedLabels.length === 0) return null;
  const failed = failedLabels.join(", ");
  return `${startedCount} of ${totalCount} exports started. Could not start: ${failed}.`;
};

export const exportSubmitButtonLabel = ({
  triggering,
  selectedCount,
  usesSyncDownload,
  usesAsyncQueue,
}: {
  triggering: boolean;
  selectedCount: number;
  usesSyncDownload: boolean;
  usesAsyncQueue: boolean;
}): string => {
  if (!triggering) {
    return selectedCount > 1
      ? `Start ${selectedCount} Exports`
      : "Start Export";
  }
  if (usesSyncDownload && usesAsyncQueue) return "Starting…";
  if (usesSyncDownload) return "Downloading…";
  return "Queuing…";
};
